import {
  Clock,
  DotsThreeVertical,
  MapPin,
  PencilSimple,
  Ticket,
  Trash,
} from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DealWithLocations } from '../../../../../../types/apiTypes';
import { VendorBusinessLocation } from '../../../../../../types/dbTypes';
import { formatDate } from '../../../../../../utils/DateUtils';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';

function PromotionsGrid({
  promotions,
  loading,
}: {
  promotions: DealWithLocations[];
  loading: boolean;
}) {
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const [popupVisibleMap, setPopupVisibleMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const togglePopup = (index: string | undefined) => {
    if (!index) return;
    setPopupVisibleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(index, !newMap.get(index)); // Toggle visibility for the item at index
      return newMap;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      let shouldClose = false;
      popupVisibleMap.forEach((isVisible, key) => {
        if (
          isVisible &&
          popupRefs.current[key] &&
          !popupRefs.current[key]?.contains(event.target as Node)
        ) {
          shouldClose = true;
        }
      });

      if (shouldClose) {
        setPopupVisibleMap(new Map()); // Close all popups if clicked outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisibleMap]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="sm:grid-cols-1lg:grid-cols-3 grid w-[1148px] max-w-[1148px] overflow-y-auto overflow-x-hidden pt-[16px] md:grid-cols-2">
            {promotions.map((promo, idx) => {
              return (
                <div
                  key={idx}
                  className="flex h-[274px] max-h-[274px] min-h-[274px] w-full flex-col gap-4 rounded-[4px] border-[1px] border-bordergray bg-white p-4"
                >
                  <div className="flex flex-row items-center justify-between align-middle">
                    <div className="flex flex-row space-x-1 rounded-[24px] border-[1px] border-bordergray bg-[#E7F8F0] px-[8px] py-[4px]">
                      <Ticket className="text-[#037355]" size={20} />
                      <h2 className="text-[12px] text-[#037355]">
                        Limited Time Offer
                      </h2>
                    </div>
                    <button
                      onClick={(e: any) => {
                        e.stopPropagation();
                        togglePopup(promo?.deal_id ?? '');
                      }}
                      className="relative"
                    >
                      <DotsThreeVertical
                        className="text-[#231F20]"
                        size={32}
                        weight="bold"
                      />
                      {popupVisibleMap.get(promo?.deal_id ?? '') && (
                        <div
                          ref={(el) => {
                            popupRefs.current[promo?.deal_id ?? ''] = el;
                          }}
                          className="absolute right-0 top-[2rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                        >
                          <div>
                            <button
                              onClick={(e: any) => {
                                e.stopPropagation();
                                togglePopup(promo?.deal_id ?? '');
                                navigate(
                                  '/dashboard/promotions/edit/' + promo?.deal_id
                                );
                              }}
                              className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                            >
                              <PencilSimple size={20} />
                              <p>Edit</p>
                            </button>
                            <button
                              onClick={(e: any) => {
                                e.stopPropagation();
                                togglePopup(promo?.deal_id ?? '');
                              }}
                              className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                            >
                              <Trash size={20} />
                              <p>Delete</p>
                            </button>
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                  <div className="flex w-full flex-col items-start space-y-2">
                    <h2 className="text-[20px] font-[700] text-primary">
                      {promo?.title}
                    </h2>
                    <h2 className="text-[16px] font-[700] text-graysecondary">
                      {promo?.description}
                    </h2>
                  </div>
                  <div className="flex flex-row items-start space-x-2 align-middle">
                    <Clock size={16} />
                    <p className="text-[12px] text-primary">
                      {(promo?.start_at !== null &&
                        formatDate(new Date(promo?.start_at ?? ''), true)) +
                        ' - ' +
                        (promo?.end_at !== null
                          ? formatDate(new Date(promo?.end_at ?? ''), true)
                          : 'Ongoing')}
                    </p>
                  </div>
                  <div className="flex flex-row items-start space-x-2 align-middle">
                    <MapPin size={16} />
                    <div className="flex h-[64px] max-h-[64px] min-h-[64px] flex-col space-y-1 overflow-y-auto overflow-x-hidden">
                      {promo?.businessLocations.map(
                        (loc: VendorBusinessLocation) => {
                          return (
                            <div className="border-[1px] border-bordergray bg-[#808080] bg-opacity-[3%] px-[6px] py-[2px] text-[12px] text-primary">
                              {loc?.address_st_number +
                                ' ' +
                                loc?.address_st_name +
                                ' ' +
                                (loc?.address_unit ?? '') +
                                ', ' +
                                loc?.address_city +
                                ', ' +
                                loc?.address_state +
                                ' ' +
                                loc?.address_zip}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default PromotionsGrid;
